import React from 'react';
import { Trophy, Edit3, Trash2, Scale, MessageSquare, AlertCircle } from 'lucide-react';
import {
  BOXING_STANCES,
  BOXING_CLASS_FORMATS,
  BOXING_MATCH_RESULTS,
  BOXING_HYDRATION,
  BOXING_CUT_METHODS,
  BOXING_PARTNER_LEVELS,
} from '../../data/combativesBoxing';

/**
 * BoxingSessionDetail — read view for a logged Boxing session.
 * Mirrors Wrestling session-detail structure with boxing-specific blocks:
 * stance, glove weight, structured combinations, rounds-based sparring
 * with per-round notes, W/L/Draw + free-text finish for matches.
 */
const BoxingSessionDetail = ({ session, onClose, onEdit, onDelete }) => {
  if (!session) return null;

  const stanceMeta = BOXING_STANCES.find((s) => s.id === session.stance) || {};
  const classFormatLabel = BOXING_CLASS_FORMATS.find((c) => c.id === session.classFormat)?.label || session.classFormat;
  const techniques = session.techniques || [];
  const combos = session.combinations || [];
  const s = session.sparring || {};
  const w = session.weight || {};
  const coach = session.coachFeedback || [];
  const matches = session.matches || [];
  const injuries = session.injuryConcerns || [];

  const cutMethodLabel = BOXING_CUT_METHODS.find((c) => c.id === w.cutMethod)?.label;
  const hydrationMeta = BOXING_HYDRATION.find((h) => h.id === w.hydrationStatus);
  const partnerLevelMeta = BOXING_PARTNER_LEVELS.find((p) => p.id === s.partnerExperienceLevel);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-enf-surface-deep border border-enf-surface-border sm:rounded-xl flex flex-col max-h-[100vh] sm:max-h-[90vh]" data-testid="boxing-session-detail">
        <div className="flex items-center justify-between p-5 border-b border-enf-surface-border flex-shrink-0">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-enf-accent-fg font-semibold mb-1 flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> Boxing session
            </div>
            <div className="text-sm text-enf-text-secondary">
              {new Date(session.date).toLocaleDateString()} · {session.duration || '—'} min · {classFormatLabel || '—'}
              {stanceMeta.label && <> · <span className="text-enf-role-info">{stanceMeta.label}</span></>}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {onEdit && (
              <button onClick={onEdit} aria-label="Edit" className="p-2 text-enf-text-muted hover:text-enf-text-primary" data-testid="boxing-detail-edit">
                <Edit3 className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button onClick={onDelete} aria-label="Delete" className="p-2 text-enf-text-muted hover:text-enf-role-warning" data-testid="boxing-detail-delete">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {onClose && (
              <button onClick={onClose} aria-label="Close" className="p-2 text-enf-text-muted hover:text-enf-text-primary text-xl leading-none" data-testid="boxing-detail-close">×</button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
          {(session.focusModes || []).length > 0 && (
            <div>
              <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Focus modes</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {session.focusModes.map((m) => <span key={m} className="px-2 py-0.5 bg-enf-surface-mid border border-enf-surface-border rounded text-xs">{m}</span>)}
              </div>
            </div>
          )}
          {session.intent && (
            <div><span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Intent</span><div className="text-enf-text-primary whitespace-pre-wrap">{session.intent}</div></div>
          )}

          {techniques.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Techniques · {techniques.length}</div>
              <div className="space-y-2">
                {techniques.map((t, i) => (
                  <div key={i} className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md" data-testid={`boxing-detail-technique-${i}`}>
                    <div className="font-semibold text-enf-text-primary">{t.name || '(unnamed technique)'}</div>
                    <div className="text-xs text-enf-text-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {t.group && <span><span className="text-enf-text-muted">Group:</span> <span className="text-enf-text-secondary capitalize">{t.group}</span></span>}
                      {t.subgroup && <span><span className="text-enf-text-muted">·</span> <span className="text-enf-text-secondary">{t.subgroup}</span></span>}
                      {t.reps && <span><span className="text-enf-text-muted">Reps:</span> <span className="text-enf-text-secondary">{t.reps}</span></span>}
                      {t.mastery && <span className="text-enf-role-info">{t.mastery}</span>}
                    </div>
                    {t.note && <div className="text-xs text-enf-text-secondary mt-2 whitespace-pre-wrap border-l-2 border-enf-surface-border pl-2">{t.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {combos.length > 0 && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Combinations · {combos.length}</div>
              <div className="space-y-1.5">
                {combos.map((c, i) => (
                  <div key={i} className="text-xs" data-testid={`boxing-detail-combo-${i}`}>
                    {c.sequence && <span className="font-mono font-semibold text-enf-text-primary mr-2">{c.sequence}</span>}
                    {c.label && <span className="text-enf-text-secondary">{c.label}</span>}
                    {c.reps && <span className="text-enf-text-muted"> · {c.reps} reps</span>}
                    {c.notes && <div className="text-enf-text-secondary mt-0.5 whitespace-pre-wrap pl-2 border-l-2 border-enf-surface-border">{c.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(s.roundsCompleted || s.cleanLandsScored || s.knockdownsScored || s.gloveWeightOz) && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Sparring</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {s.roundsCompleted && <div><span className="text-enf-text-muted">Rounds:</span> <span className="font-semibold">{s.roundsCompleted}</span></div>}
                {s.roundLengthSec && <div><span className="text-enf-text-muted">Round len:</span> <span className="font-semibold">{s.roundLengthSec}s</span></div>}
                {s.gloveWeightOz && <div><span className="text-enf-text-muted">Gloves:</span> <span className="font-semibold">{s.gloveWeightOz} oz</span></div>}
                {s.intensityRating && <div><span className="text-enf-text-muted">Intensity:</span> <span className="font-semibold">{s.intensityRating}/5</span></div>}
                {partnerLevelMeta && <div className="col-span-2"><span className="text-enf-text-muted">Partner:</span> <span className="font-semibold">{partnerLevelMeta.label}</span></div>}
                {s.cleanLandsScored && <div><span className="text-enf-text-muted">Clean lands ✓:</span> <span className="font-semibold">{s.cleanLandsScored}</span></div>}
                {s.cleanLandsConceded && <div><span className="text-enf-text-muted">Clean lands ✗:</span> <span className="font-semibold">{s.cleanLandsConceded}</span></div>}
                {s.knockdownsScored && <div><span className="text-enf-text-muted">KDs ✓:</span> <span className="font-semibold">{s.knockdownsScored}</span></div>}
                {s.knockdownsConceded && <div><span className="text-enf-text-muted">KDs ✗:</span> <span className="font-semibold">{s.knockdownsConceded}</span></div>}
              </div>
              {(s.rounds || []).length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Per-round notes</div>
                  {s.rounds.map((r, i) => (
                    <div key={i} className="text-xs">
                      <span className="font-semibold text-enf-text-primary">R{r.roundNum || i + 1}:</span>{' '}
                      <span className="text-enf-text-secondary whitespace-pre-wrap">{r.note}</span>
                    </div>
                  ))}
                </div>
              )}
              {s.note && <div className="text-xs text-enf-text-secondary mt-2 whitespace-pre-wrap border-l-2 border-enf-surface-border pl-2">{s.note}</div>}
            </div>
          )}

          {coach.length > 0 && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Coach feedback</div>
              <div className="space-y-1.5">
                {coach.map((c, i) => (
                  <div key={i} className="text-xs">
                    {c.topic && <span className="px-1.5 py-0.5 bg-enf-surface-deep border border-enf-surface-border rounded text-[10px] mr-2 text-enf-role-info">{c.topic}</span>}
                    <span className="text-enf-text-secondary whitespace-pre-wrap">{c.note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {matches.length > 0 && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2">Matches · {matches.length}</div>
              <div className="space-y-2">
                {matches.map((m, i) => {
                  const resultMeta = BOXING_MATCH_RESULTS.find((r) => r.id === m.result);
                  const resultColor = m.result === 'win' ? 'text-enf-role-success' : m.result === 'loss' ? 'text-enf-accent-fg' : 'text-enf-text-muted';
                  return (
                    <div key={i} className="text-xs">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`font-semibold uppercase tracking-wider ${resultColor}`}>{resultMeta?.label || m.result}</span>
                        {m.opponent && <span className="text-enf-text-primary">vs {m.opponent}</span>}
                        {m.finish && <span className="text-enf-text-muted">· {m.finish}</span>}
                      </div>
                      {m.notes && <div className="text-enf-text-secondary whitespace-pre-wrap pl-2 border-l-2 border-enf-surface-border">{m.notes}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(w.scaleWeightLb || w.hydrationStatus || w.targetClassLb) && (
            <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2 flex items-center gap-1.5"><Scale className="w-3 h-3" /> Weight & hydration</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {w.scaleWeightLb && <div><span className="text-enf-text-muted">Today:</span> <span className="font-semibold">{w.scaleWeightLb} lb</span></div>}
                {w.targetClassLb && <div><span className="text-enf-text-muted">Target:</span> <span className="font-semibold">{w.targetClassLb} lb</span></div>}
                {w.daysToWeighIn && <div><span className="text-enf-text-muted">Days out:</span> <span className="font-semibold">{w.daysToWeighIn}</span></div>}
                {cutMethodLabel && <div className="col-span-2"><span className="text-enf-text-muted">Method:</span> <span className="font-semibold">{cutMethodLabel}</span></div>}
                {hydrationMeta && (
                  <div className="col-span-2 sm:col-span-3">
                    <span className="text-enf-text-muted">Hydration:</span>{' '}
                    <span className={`font-semibold ${
                      hydrationMeta.id === 'red' ? 'text-enf-accent-fg' :
                      hydrationMeta.id === 'amber' ? 'text-enf-role-warning' :
                      'text-enf-role-success'
                    }`}>{hydrationMeta.label}</span>
                    <span className="text-enf-text-muted"> — {hydrationMeta.help}</span>
                  </div>
                )}
              </div>
              {w.notes && <div className="text-xs text-enf-text-secondary mt-2 whitespace-pre-wrap border-l-2 border-enf-surface-border pl-2">{w.notes}</div>}
            </div>
          )}

          {injuries.length > 0 && (
            <div className="p-3 bg-enf-role-danger/10 border border-enf-role-danger/30 rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-2 flex items-center gap-1.5"><AlertCircle className="w-3 h-3 text-enf-accent-fg" /> Injury concerns</div>
              <div className="space-y-1 text-xs">
                {injuries.map((j, i) => (
                  <div key={i}>
                    <span className="font-semibold text-enf-text-primary">{j.area || 'Area not noted'}</span>
                    {j.severity && <span className="text-enf-text-muted"> · severity {j.severity}/5</span>}
                    {j.note && <div className="text-enf-text-secondary mt-0.5 whitespace-pre-wrap">{j.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {session.reflection && (
            <div>
              <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Reflection</span>
              <div className="text-enf-text-secondary whitespace-pre-wrap">{session.reflection}</div>
            </div>
          )}

          {session.notes && (
            <div><span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold">Notes</span><div className="text-enf-text-secondary whitespace-pre-wrap">{session.notes}</div></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoxingSessionDetail;
